'use client';

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSWRConfig } from 'swr';
import { clsx } from 'clsx';
import { useTranslation } from 'next-i18next';
import { FileUploadZone, formatFileSize } from '@components/features/screening';
import type { CvFile } from '@components/features/screening';
import {
  isApiV1Errors,
  useFileUpload,
  useSwrImportJob,
} from '@hooks/useSwrApi';
import { Button, Modal, TailwindSpinner } from '@components/common';
import { mcFlowColumnStatusesQueryMap } from '@components/templates/employer/applicant-list/flow-mc/constants';
import { fetcher } from '@utils/data-fetching/fetcher';
import type { ApiSchema } from '@type/api/apiUtilityFn';
import {
  CheckCircleIcon,
  DocumentTextIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';

interface McCvUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  jobSlug: string;
  onApplicantsColumnRevalidate?: () => void | Promise<unknown>;
}

const statusConfig = {
  processing: {
    labelKey: 'screening.processing.label',
    badgeClassName: 'bg-secondary/10 text-secondary',
    iconClassName: 'text-secondary',
  },
  pending: {
    labelKey: 'screening.processing.label',
    badgeClassName: 'bg-secondary/10 text-secondary',
    iconClassName: 'text-secondary',
  },
  matching: {
    labelKey: 'screening.processing.label',
    badgeClassName: 'bg-secondary/10 text-secondary',
    iconClassName: 'text-secondary',
  },
  completed: {
    labelKey: 'global.label.success',
    badgeClassName: 'bg-primary/10 text-primary',
    iconClassName: 'text-primary',
  },
  error: {
    labelKey: 'global.general.error.title',
    badgeClassName: 'bg-error/10 text-error',
    iconClassName: 'text-error',
  },
} as const;

export const McCvUploadModal = ({
  isOpen,
  onClose,
  jobSlug,
  onApplicantsColumnRevalidate,
}: McCvUploadModalProps) => {
  const { t } = useTranslation('common');
  const [cvFiles, setCvFiles] = useState<CvFile[]>([]);
  const [keepOpenUntilUploadSettles, setKeepOpenUntilUploadSettles] = useState(false);
  const [hasUploadError, setHasUploadError] = useState(false);
  const [isScreeningJobReady, setIsScreeningJobReady] = useState(false);
  const { mutate } = useSWRConfig();
  const { trigger: importJob } = useSwrImportJob();

  const revalidateApplicantsColumn = useCallback(() => {
    const applicantsStatuses = mcFlowColumnStatusesQueryMap.applicants;

    void onApplicantsColumnRevalidate?.();

    void mutate(
      (key: unknown) => {
        if (!Array.isArray(key)) return false;

        const firstKeyPart: unknown = key[0];
        if (typeof firstKeyPart !== 'string') return false;
        const url = firstKeyPart;
        if (!url.startsWith(`/v1/profile/${jobSlug}/list?`)) return false;

        const queryString = url.split('?')[1];
        if (!queryString) return false;

        const params = new URLSearchParams(queryString);

        return params.get('statuses') === applicantsStatuses
          && params.get('withPlaceholders') === 'true';
      },
      undefined,
      { revalidate: true },
    );
  }, [jobSlug, mutate, onApplicantsColumnRevalidate]);

  const resetState = useCallback(() => {
    setCvFiles([]);
    setKeepOpenUntilUploadSettles(false);
    setHasUploadError(false);
    setIsScreeningJobReady(false);
  }, []);

  const handleClose = useCallback(() => {
    if (keepOpenUntilUploadSettles) return;

    resetState();
    onClose();
  }, [keepOpenUntilUploadSettles, onClose, resetState]);

  const ensureScreeningJobExists = useCallback(async () => {
    if (isScreeningJobReady) return;

    const existingJob = await fetcher<ApiSchema<'JobOutput'>>(
      `/v1/screening/jobs/by-slug/${jobSlug}`,
      undefined,
    );

    if (existingJob && !isApiV1Errors(existingJob)) {
      setIsScreeningJobReady(true);
      return;
    }

    const importedJob = await importJob({ jobSlug });

    if (!importedJob || isApiV1Errors(importedJob)) {
      throw new Error('screening import failed');
    }

    setIsScreeningJobReady(true);
  }, [importJob, isScreeningJobReady, jobSlug]);

  const {
    isDragActive,
    isUploading,
    handleDrag,
    handleDrop,
    handleFileInput,
  } = useFileUpload({
    cvFiles,
    setCvFiles,
    jobSlug,
    beforeUpload: ensureScreeningJobExists,
    onUploadStart: () => {
      setHasUploadError(false);
      setKeepOpenUntilUploadSettles(true);
    },
  });

  useEffect(() => {
    if (!isOpen) {
      resetState();
    }
  }, [isOpen, resetState]);

  useEffect(() => {
    if (!keepOpenUntilUploadSettles || isUploading) return;

    const hasErrorState = cvFiles.some((file) => file.status === 'error');

    if (hasErrorState) {
      setHasUploadError(true);
      setKeepOpenUntilUploadSettles(false);
      return;
    }

    revalidateApplicantsColumn();
    resetState();
    onClose();
  }, [cvFiles, isUploading, keepOpenUntilUploadSettles, onClose, revalidateApplicantsColumn, resetState]);

  const uploadRows = useMemo(
    () => cvFiles.map((file) => ({
      ...file,
      statusMeta: statusConfig[file.status],
    })),
    [cvFiles],
  );
  const renderUploadStatusIcon = (file: (typeof uploadRows)[number]) => {
    if (file.status === 'error') {
      return <ExclamationCircleIcon className={clsx('size-4', file.statusMeta.iconClassName)} />;
    }
    if (file.status === 'completed') {
      return <CheckCircleIcon className={clsx('size-4', file.statusMeta.iconClassName)} />;
    }

    return <TailwindSpinner className={clsx('size-3.5', file.statusMeta.iconClassName)} />;
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="lg"
      backdropClickDisabled={keepOpenUntilUploadSettles}
    >
      <Modal.Header onClose={handleClose}>
        {t('my-jobs.card.actions.upload-cvs')}
      </Modal.Header>

      <Modal.Body className="space-y-4 p-6">
        {keepOpenUntilUploadSettles && (
          <div className="flex items-center gap-3 rounded-2xl border border-secondary/15 bg-secondary/5 px-4 py-3 text-sm font-medium text-ink">
            <TailwindSpinner className="size-4 shrink-0 text-secondary" />
            {t('screening.processing.cvs.label')}
          </div>
        )}

        {hasUploadError && (
          <div className="rounded-2xl border border-error/20 bg-error/5 px-4 py-3 text-sm text-ink">
            {t('global.general.error.try-again')}
          </div>
        )}

        <div className={clsx(keepOpenUntilUploadSettles && 'pointer-events-none opacity-60')}>
          <FileUploadZone
            isDragActive={isDragActive}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onFileInput={handleFileInput}
          />
        </div>

        {uploadRows.length > 0 && (
          <div className="space-y-2">
            {uploadRows.map((file) => (
              <div
                key={file.id}
                className="flex items-center gap-3 rounded-2xl border border-input/60 bg-surface-50 px-4 py-3"
              >
                <DocumentTextIcon className="size-5 shrink-0 text-ink-medium" />

                <div className="min-w-0 grow">
                  <p className="truncate text-sm font-medium text-ink">{file.name}</p>
                  <p className="text-xs text-ink-medium">{formatFileSize(file.size)}</p>
                </div>

                <div
                  className={clsx(
                    'inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold',
                    file.statusMeta.badgeClassName,
                  )}
                >
                  {renderUploadStatusIcon(file)}
                  {t(file.statusMeta.labelKey)}
                </div>
              </div>
            ))}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="flex justify-end border-t border-input bg-surface-50 px-6 py-4">
        <Button
          type="button"
          styling="solid"
          color="light"
          onClick={handleClose}
          disabled={keepOpenUntilUploadSettles}
        >
          {t('global.action.cancel')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
